<!-- THE STAGE — like home, this route extends the stage scene the chrome
     started (route data stageExtension:true drops the shell handoff). The hero
     holds the page's single <h1> as REAL TEXT: the wordmark and the tagline
     used to be pixels baked into public/images/Logo-2-jpeg.webp, which is the
     SEO/a11y defect roadmap #72 exists to remove. The lockup below IS that
     image's content, promoted to markup. -->
<div data-scene="stage" class="x-jazz-stage">
  <!-- The figure swaps aspect between stacked (5/4 crop) and side-by-side
       (portrait, capped) — see the SCSS. That makes its rendered width vary from
       ~100vw to ~36rem, so it needs resolution switching or it is upscaled at 2x
       on tablets. 900w is what phones fetch; 1800w only lands on wide 2x panels. -->
  <groove-hero
    layout="split"
    src="/images/x-jazz/hero-russ-1300.webp"
    srcset="
      /images/x-jazz/hero-russ-900.webp   900w,
      /images/x-jazz/hero-russ-1300.webp 1300w,
      /images/x-jazz/hero-russ-1800.webp 1800w
    "
    sizes="(max-width: 56.25rem) 100vw, 36rem"
    objectPosition="50% 32%"
    alt="Russ Rodgers playing his six-string bass guitar, seated between two cymbals at an outdoor event"
  >
    <p class="x-jazz-lockup">
      <span class="x-jazz-wordmark">X-Ploration Jazz</span>
      <span class="x-jazz-tagline"
        >A Touch of Class for Your Business Event</span
      >
    </p>
    <h1 class="groove-type-display-lg x-jazz-headline">
      Live jazz for corporate events and <em>private gatherings.</em>
    </h1>
    <p class="x-jazz-lede">
      A three to five piece group I lead, with
      <strong>over fifty years</strong> of professional playing. Available
      around South Florida.
    </p>
    <div class="x-jazz-hero-actions">
      <a grooveAction [href]="phoneHref">Call Russ</a>
      <a grooveAction variant="ghost" [href]="emailHref"> or send an email </a>
    </div>
  </groove-hero>
</div>

<!-- Stage → paper handoff. Placed in paper (root) scope so
     .groove-handoff { color: var(--groove-color-bg-page) } resolves to the
     PAPER bg the wave fills (see groove-scene.css); the local SCSS re-points it
     at bg-surface because the first paper beat below is a recessed band. -->
<div class="groove-handoff" aria-hidden="true">
  <svg viewBox="0 0 1440 110" preserveAspectRatio="none">
    <path
      fill="currentColor"
      d="M0,110 L0,70 C240,30 480,95 720,60 C960,25 1200,80 1440,45 L1440,110 Z"
    />
  </svg>
</div>

<!-- THE GROUP — first paper beat, a full-bleed recessed band (home's
     .home-meet shape). Ensemble scaling in PROSE with real instrumentation and
     a use-case per size. Deliberately not a comparison table and deliberately
     not named packages: no booking page in the reference corpus used either,
     and the one that named tiers read cheaper than its client list. -->
<section class="x-jazz-group">
  <div class="x-jazz-group-inner">
    <p class="x-jazz-kicker">The <span class="groove-arc-swash">group</span></p>
    <h2 class="x-jazz-group-heading groove-type-display">
      Sized to the room, from a duo to a quintet.
    </h2>
    <div class="x-jazz-group-body groove-flow">
      <p>
        A duo, bass and keys, suits a cocktail hour or a small reception where
        the music belongs under the conversation. Add drums and you have the
        trio, the configuration I book most often: enough to swing, small enough
        to keep a room talking.
      </p>
      <p>
        A quartet brings in a horn or a vocalist for a longer evening. The
        quintet handles an awards night, a gala, or a feature set that closes
        out the room after dinner.
      </p>
      <p>
        The repertoire covers jazz standards and contemporary instrumentals,
        drawn from a deep book. I will work with you on a setlist that fits the
        event: background music through dinner or breaks, something with more
        energy later in the evening, or a full performance to cap off the night.
      </p>
    </div>
  </div>
</section>

<!-- HOW IT PLAYS — open paper, same kicker/heading/body split as the two beats
     around it. Event types in the vocabulary corporate planners actually use
     (no emoji grid, no birthdays-and-anniversaries consumer list). -->
<div class="x-jazz-paper">
  <section class="x-jazz-play">
    <p class="x-jazz-kicker">
      How it <span class="groove-arc-swash">plays</span>
    </p>
    <h2 class="x-jazz-play-heading groove-type-display">
      Music that sets the tone without taking over the room.
    </h2>
    <div class="x-jazz-play-body groove-flow">
      <p>
        Receptions and cocktail hours. Corporate meetings and conferences.
        Holiday parties, award nights, product launches, executive retreats, and
        private dinners. The musicians I work with have years of experience
        playing for corporate and private clients, and they know the difference
        between a room that wants to listen and a room that wants to talk.
      </p>
      <p>
        Energy when you want it, out of the way when your guests need to talk.
        The group travels. Tell me the date, the room, and the mood you are
        after, and I will tell you what fits.
      </p>
    </div>
  </section>
</div>

<!-- HEAR THE GROUP — open paper, same editorial split as the beats around it.
     The listen/watch proof beat: a booking page can describe the music, but a
     planner deciding between acts wants to hear it. The playlist collects dates
     Russ plays bass on under other leaders' names (roadmap #76 tracks that
     channel-side). Russ named this specific video on the 2026-08-11 call,
     replacing the Peter Edelman "I Should Care" pick Jeremy had made — see
     docs/russ-call-2026-08-12.md outcome 8. Because his pick is a vocal
     feature rather than a piano/bass/drums date, the kicker and heading say
     "group" rather than "trio"; the trio-as-default claim still lives in the
     booking beats below. Russ confirmed 2026-08-11 that the group does add a
     singer for events, so the vocalist sentence is his, not an inference. -->
<div class="x-jazz-paper">
  <section class="x-jazz-listen">
    <p class="x-jazz-kicker">
      Hear the <span class="groove-arc-swash">group</span>
    </p>
    <h2 class="x-jazz-listen-heading groove-type-display">
      The group, on a club date.
    </h2>
    <div class="x-jazz-listen-body groove-flow">
      <p>
        &quot;Mean To Me&quot;, with vocalist Flo Anito out front. The group
        adds a singer when an event calls for one. More performances are on the
        <a
          href="https://www.youtube.com/playlist?list=PLnRW_LWLpwnfXmSLmlep38sLXiv-NW4Rr"
          target="_blank"
          rel="noopener"
          >X-Ploration Jazz playlist</a
        >.
      </p>
      <app-youtube-section resource="video:QC1mb3vwon0" />
    </div>
  </section>
</div>

<!-- WHO LEADS THE GROUP — open paper, the two-column editorial split home uses
     for "How Russ teaches". This is the credibility beat, and it stands in for
     the client-logo wall this page does not have. Every claim here is Russ's
     own, already published on /about/performance; the stat strip carries the
     two that matter most to a corporate booker. -->
<div class="x-jazz-paper">
  <section class="x-jazz-leader">
    <p class="x-jazz-kicker">
      Who leads the <span class="groove-arc-swash">group</span>
    </p>
    <h2 class="x-jazz-leader-heading groove-type-display">
      You are booking the musician who leads the group.
    </h2>
    <div class="x-jazz-leader-body groove-flow">
      <p>
        I built my career in Atlanta. I founded the Bass Program at the Atlanta
        Institute of Music in 1985 and spent close to three decades on the
        city's jazz and session scene, including eight years as the house
        bassist for the Ritz-Carlton Jazz Trio, along with studio work on
        national radio and television spots.
      </p>
      <p>
        Then twelve years in the Washington DC region, working across DC,
        Maryland, and Northern Virginia. It is one of the toughest rooms in the
        country for a working jazz player: the bar is high, the players are
        serious, and nobody keeps calling you back unless you can hold your own
        every night. That is where X-Ploration Jazz took its current shape.
      </p>
      <p>
        When you call, you reach me directly. There is a fuller account on the
        <a routerLink="/about/bio">bio</a> page, and the artists I have recorded
        and performed with are listed on
        <a routerLink="/about/credits">credits</a>.
      </p>
      <div grooveStatStrip class="x-jazz-stats">
        <div><b>50+</b> years on the bass</div>
        <div><b>8</b> years house bassist, Ritz-Carlton Jazz Trio</div>
        <div><b>1985</b> founded the AIM bass program</div>
      </div>
    </div>
  </section>

  <!-- BOOKING — the direct phone/email block. This site has no contact form by
       design; <app-contact-details> is the conversion surface. -->
  <section class="x-jazz-booking" aria-labelledby="x-jazz-booking-heading">
    <p class="x-jazz-kicker">
      <span class="groove-arc-swash">Booking</span>
    </p>
    <h2
      id="x-jazz-booking-heading"
      class="x-jazz-booking-heading groove-type-display"
    >
      Tell me about your event.
    </h2>
    <p>
      Whether you are booking background music during breaks or a full
      performance to cap off the evening, the group scales to fit. To talk
      through what your event needs, reach out directly:
    </p>
    <app-contact-details />
  </section>

  <!-- EXPLORE — Groove wayfinding tiles (app-page-explore reads the route's
       data.related keys from RouteUiDataService). Rendered here rather than by
       the shell because this route suppresses the shared footer CTA pair. -->
  <div class="x-jazz-page-explore">
    <app-page-explore />
  </div>
</div>

<!-- REPRISE — the closing conversion block returns to the dark stage, the
     single site-wide bottom-CTA treatment. The .x-jazz-paper wrapper re-applies
     the shell gutter the host stripped, so the spotlight card sits inset. -->
<div class="x-jazz-paper x-jazz-reprise">
  <app-page-cta
    location="bottom"
    heading="Book jazz for your event"
    body="Live jazz for receptions, galas, and private dinners around South Florida. Reach out by phone or email and we'll talk through your event."
  />
</div>
